import type { IconNames } from '@afterpaytouch/core'
import { PortalApiError } from '@afterpaytouch/portal-api/types'

export const CASH_COHORT_CONVERGENCE_GENERIC_INSTRUCTION_KEY = 'cashCohortConvergenceRemediation:instructions.generic'

const CASH_COHORT_CONVERGENCE_VIOLATION_INSTRUCTION_KEYS = {
  ACQUIRE_LINKED_DEBIT_CARD: 'cashCohortConvergenceRemediation:instructions.acquireLinkedDebitCard',
  ACQUIRE_SMS_ALIAS: 'cashCohortConvergenceRemediation:instructions.acquireSmsAlias',
  ACQUIRE_EMAIL_ALIAS: 'cashCohortConvergenceRemediation:instructions.acquireEmailAlias',
  IDV: 'cashCohortConvergenceRemediation:instructions.idv',
  COLLECT_ADDRESS: 'cashCohortConvergenceRemediation:instructions.collectAddress',
  PROVIDE_LEGAL_NAME: 'cashCohortConvergenceRemediation:instructions.idv',
} as const

export const CASH_COHORT_CONVERGENCE_VIOLATION_DISPLAY_ORDER = [
  'ACQUIRE_LINKED_DEBIT_CARD',
  'ACQUIRE_SMS_ALIAS',
  'ACQUIRE_EMAIL_ALIAS',
  'IDV',
  'COLLECT_ADDRESS',
  'PROVIDE_LEGAL_NAME',
] as const

export type CashCohortConvergenceViolationCode = keyof typeof CASH_COHORT_CONVERGENCE_VIOLATION_INSTRUCTION_KEYS

export type CashCohortConvergenceInstructionKey =
  | (typeof CASH_COHORT_CONVERGENCE_VIOLATION_INSTRUCTION_KEYS)[CashCohortConvergenceViolationCode]
  | typeof CASH_COHORT_CONVERGENCE_GENERIC_INSTRUCTION_KEY

export const CASH_COHORT_CONVERGENCE_INSTRUCTION_ICON_NAMES: Record<CashCohortConvergenceInstructionKey, IconNames> = {
  'cashCohortConvergenceRemediation:instructions.acquireLinkedDebitCard': 'Card',
  'cashCohortConvergenceRemediation:instructions.acquireSmsAlias': 'Phone',
  'cashCohortConvergenceRemediation:instructions.acquireEmailAlias': 'Email',
  'cashCohortConvergenceRemediation:instructions.idv': 'Profile',
  'cashCohortConvergenceRemediation:instructions.collectAddress': 'Location',
  [CASH_COHORT_CONVERGENCE_GENERIC_INSTRUCTION_KEY]: 'Info',
}

export type CashCohortConvergenceRemediationParseResult = { status: 'terminal' } | { status: 'remediable'; violations: string[] }

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

const isStringArray = (value: unknown): value is string[] => Array.isArray(value) && value.every((entry) => typeof entry === 'string')

export const isCashCohortConvergenceViolationCode = (value: string): value is CashCohortConvergenceViolationCode => {
  return value in CASH_COHORT_CONVERGENCE_VIOLATION_INSTRUCTION_KEYS
}

export const getCashCohortConvergenceRecognizedViolations = (violations: string[]): CashCohortConvergenceViolationCode[] => {
  const violationSet = new Set(violations.filter(isCashCohortConvergenceViolationCode))

  return CASH_COHORT_CONVERGENCE_VIOLATION_DISPLAY_ORDER.filter((violation) => violationSet.has(violation))
}

export const getCashCohortConvergenceInstructionKeys = (violations: string[]): CashCohortConvergenceInstructionKey[] => {
  const violationSet = new Set(violations)
  const instructionKeys = CASH_COHORT_CONVERGENCE_VIOLATION_DISPLAY_ORDER.filter((violation) => violationSet.has(violation)).map(
    (violation) => CASH_COHORT_CONVERGENCE_VIOLATION_INSTRUCTION_KEYS[violation]
  )
  const hasUnknownViolation = violations.some((violation) => !(violation in CASH_COHORT_CONVERGENCE_VIOLATION_INSTRUCTION_KEYS))

  if (instructionKeys.length > 0 && hasUnknownViolation) {
    return [...instructionKeys, CASH_COHORT_CONVERGENCE_GENERIC_INSTRUCTION_KEY]
  }

  return instructionKeys
}

export const parseCashCohortConvergenceRemediation = (error: PortalApiError): CashCohortConvergenceRemediationParseResult => {
  const errorInfo = (error as { errorInfo?: unknown }).errorInfo

  if (!isRecord(errorInfo) || typeof errorInfo.remediable !== 'boolean') {
    return { status: 'remediable', violations: [] }
  }

  if (!errorInfo.remediable) {
    return { status: 'terminal' }
  }

  if (!isStringArray(errorInfo.violations)) {
    return { status: 'remediable', violations: [] }
  }

  return { status: 'remediable', violations: errorInfo.violations }
}
